/**
 * Catalog of every glTF model bundled in `public/models/3d/`. The
 * in-3D "Drop 3D model" browser (Asset3DBrowser) reads this list to
 * render the categorised picker; the Scene3D entity loop uses it to
 * resolve `obstacle.modelUri` → metadata (native height, default scale,
 * etc.) when rendering a placed model.
 *
 * Entries are "single" models — one prop, ready to drop at one location.
 *
 * ⛔ NO PACKS. A "pack" .glb bundles many meshes (a row of buildings, a fleet of
 * cars) and `isPack` is a UI BADGE ONLY — nothing extracts a single item, so
 * dropping one loads the entire bundle's geometry AND textures. Every pack this
 * catalog carried was removed on 2026-08-17 after none had ever been placed in
 * 3 391 production projects while costing hundreds of megabytes of VRAM apiece.
 * The field survives so a future pack could be labelled, but do not add one
 * without a mesh-level picker in front of it.
 *
 * Adding a new model: drop the .glb into the right category dir under
 * `public/models/3d/<category>/`, then add an entry below. The browser
 * picks it up automatically. Use lowercase-hyphen ids. Check the texture count
 * first — VRAM is dimensions × count, and has nothing to do with file size.
 *
 * Licensing:
 *   - Kenney (car-kit, nature-kit, blocky-characters): CC0
 *   - User-downloaded GLBs from various Sketchfab / Poly Pizza authors:
 *     check each model's source page for the exact CC variant (CC0 /
 *     CC-BY) — credits not yet wired up here but each `source` field
 *     documents where the file came from for the future credits page.
 */

export type AssetCategory =
  | 'camera'
  | 'vehicle'
  | 'machinery'
  | 'tree'
  | 'person'
  | 'building'
  | 'prop';

export interface AssetModel {
  /** Stable lowercase-hyphen id (used in `obstacle.modelUri`). */
  id: string;
  /** Display name in the browser UI. */
  label: string;
  category: AssetCategory;
  /** Path served by Vite from `public/`. `/models/3d/...` */
  uri: string;
  /** Approximate native model height in metres — used as a sanity
   *  check when sizing in the FOV blocker footprint. */
  nativeHeightM: number;
  /** Default scale multiplier when the model is placed. 1.0 = native
   *  size from the .glb (Kenney models are authored at 1 unit = 1 m). */
  defaultScale: number;
  /** Whether this .glb bundles multiple meshes (treat as backdrop, not
   *  a single-object prop). */
  isPack?: boolean;
  /** Approx file size in MB. Shown next to large models so users know
   *  why their drop takes a few seconds the first time. */
  sizeMB: number;
  /** License + source URL/name for the future credits page. */
  license: string;
  source: string;
}

/** Surveillance-tower glTF model URI. The tower is no longer browsable
 *  in the 3D Library — it's inserted via the 2D designer's Insert →
 *  "Surveillance tower" flow (which opens a config dialog and spawns
 *  cameras + equipment). This constant is the visual model used for the
 *  tower obstacle the spawn creates.
 *
 *  v2 (2026-08-28) is procedural, 88 KB vs the legacy 29 MB photogrammetry
 *  scan, and carries named nodes for a PARAMETRIC mast: `Mast` is a
 *  unit-height section (scale Y to stretch), `Head` is the camera head
 *  (translate to the mast top), `BrandPanel` wraps three cabinet walls.
 *  Generator + interactive preview: tools/tower-model/. */
export const SURVEILLANCE_TOWER_MODEL_URI = '/models/3d/cameras/solar-tower-v2.glb';

/** Models that no longer ship, and what to draw instead. An obstacle stores
 *  the URI it was placed with, so deleting a file would leave old projects
 *  loading a 404 and showing nothing where a tower or a car used to be. Every
 *  read of `modelUri` goes through `resolveModelUri` instead, which means a
 *  retired file costs one line here rather than a migration over saved
 *  documents. The legacy tower was a 29 MB photogrammetry scan and the legacy
 *  sedan was modelled on a 45 degree diagonal, so its own footprint was
 *  4.89 x 4.93 m: both are replaced by better models of the same thing. */
const RETIRED_MODEL_URIS: Record<string, string> = {
  '/models/3d/cameras/hera-solar-tower.glb': '/models/3d/cameras/solar-tower-v2.glb',
  '/models/3d/cars/car-sedan-nice.glb': '/models/3d/cars/sedan.glb',
};

export function resolveModelUri(uri: string): string {
  return RETIRED_MODEL_URIS[uri] ?? uri;
}
export const SURVEILLANCE_TOWER_NATIVE_HEIGHT_M = 6.0;

/** Mast-height range offered in the tower dialog and edit panel, meters.
 *  The value is the CAMERA MOUNTING height — what the FOV math consumes. */
export const TOWER_MAST_MIN_M = 3;
export const TOWER_MAST_MAX_M = 12;
export const TOWER_MAST_DEFAULT_M = 6;

/** Default optics for the cameras a tower spawn creates. A tower exists for
 *  perimeter coverage, so the default has to LOOK like coverage on drop —
 *  the generic 20 m dome default read as confetti around the marker. */
export const TOWER_CAMERA_RANGE_M = 45;
export const TOWER_CAMERA_HFOV_DEG = 62;

/** Per-camera FOV colours for a tower. All tower cameras sit on ONE mast at
 *  one point (owner rule 2026-08-28), so colour is what tells their coverage
 *  wedges apart — a single green for all of them read as one blob. */
export const TOWER_CAMERA_COLORS = [
  '#22c55e', // green
  '#3b82f6', // blue
  '#f59e0b', // amber
  '#a855f7', // purple
  '#ef4444', // red
  '#14b8a6', // teal
  '#eab308', // yellow
  '#ec4899', // pink
] as const;

/** v2 model internals, must match tools/tower-model/build-tower.mjs:
 *  the mast section starts at MAST_BASE_Y and is authored DEFAULT_LEN tall
 *  (already baked as the node's scale); the Head node's authored origin sits
 *  at HEAD_DEFAULT_Y. Scene3D composes Cesium nodeTransformations ON TOP of
 *  those authored transforms to reach the requested mast height. */
export const TOWER_V2_MAST_BASE_Y = 0.92;
export const TOWER_V2_MAST_DEFAULT_LEN = 3.63;
export const TOWER_V2_HEAD_DEFAULT_Y = 4.55;
/** Head origin sits ~0.1 m under the camera arm axis. */
export const TOWER_V2_HEAD_TO_CAMERA_M = 0.1;

/**
 * ── What was removed on 2026-08-17, and why ─────────────────────────────────
 *
 * The catalog used to carry 26 models. A usage audit against production —
 * 3 391 saved projects, matching `"modelUri":\s*"..."` — found that TWENTY-FOUR
 * of them had never been placed by anybody, ever, while accounting for 99.9 MB
 * of the 131 MB shipped in `public/models` on every deploy.
 *
 * They were not merely idle. The heaviest were a hazard on weak hardware,
 * because nothing splits a single item out of a pack — `isPack` is a UI badge,
 * so dropping one loads the whole bundle. Measured from the glTF headers:
 * `cctv-set-pack.glb` was 71 textures at 1024², about 378 MB of VRAM for 6 728
 * triangles, sitting in the CAMERA category where it was the likeliest thing to
 * be clicked first. `passenger-car-pack.glb` was another 207 MB.
 *
 * What is left is what production actually uses: the two Sketchfab cars, which
 * the obstacle tool also renders for every placed `car` (176 placements) via
 * `VehicleGeometry`. Everything else a user places — trees, shrubs, trucks, the
 * DORI person, the surveillance tower — resolves its .glb by CONSTRUCTED path
 * (`treeSpecies.ts`, `shrubSpecies.ts`, `VehicleGeometry.ts`, `PersonGeometry.ts`,
 * `SURVEILLANCE_TOWER_MODEL_URI` below) and never appeared in this list.
 *
 * No saved project referenced any deleted file, so nothing in user data breaks.
 * Files are git-tracked; restoring one means `git checkout` plus an entry here.
 */

/** ─── CAMERAS / ALARM ──────────────────────────────────────────────
 *  Emptied by the 2026-08-17 audit: `camera-detailed`, `cameras-pack-a`,
 *  `cctv-set-pack` (the 378 MB-VRAM one) and `alarm-panel`, none ever placed. */
const CAMERAS: AssetModel[] = [];

/** ─── VEHICLES — Sketchfab CC-BY (real low-poly cars) ──────────────
 *  All Kenney "toy" trucks were removed at user's request: they looked
 *  clearly stylised next to the realistic Sketchfab cars. Their replacements
 *  (2026-08-24) are the four entries below, cut out of one Sketchfab pack.
 *
 *  Everything here earns its place twice over: `VehicleGeometry` renders these
 *  same files for every `car` and `truck` obstacle, so they ship whether or
 *  not this browser lists them, and listing them costs nothing extra. The
 *  three packs (`passenger-car-pack`, `police-pack`, `trucks-pack`) went. */
const VEHICLES: AssetModel[] = [
  { id: 'car-wagon-nice',     label: 'Station wagon (Passat B6)', category: 'vehicle', uri: '/models/3d/cars/car-wagon-nice.glb',   nativeHeightM: 1.50, defaultScale: 1,    sizeMB: 0.6, license: 'CC-BY', source: 'Sketchfab — generic_usaeu_station_wagon' },
  // Textureless, so the whole set costs four materials and no VRAM for
  // imagery. Native sizes are true to life at defaultScale 1.
  /** Generic passenger car pack, one Sketchfab CC-BY download (Comrade1280)
   *  cut into ten separately placeable cars. Conformed by the split script:
   *  1 unit = 1 m at true size, origin on the ground, nose on -Z, so every one
   *  takes defaultScale 1 and needs no headingOffset. The pack lays its cars
   *  out in a ring all facing the centre, which is what recovers each car's
   *  facing; a principal-axis fit alone gives the axis but not the direction.
   *  Textures were resized to 512 px and recompressed: 18 MB of duplicated
   *  atlases across the ten became 4.2 MB. */
  { id: 'car-compact',     label: 'Compact hatchback (3.7 m)',   category: 'vehicle', uri: '/models/3d/cars/compact.glb',       nativeHeightM: 1.49, defaultScale: 1,    sizeMB: 0.47, license: 'CC-BY', source: 'Sketchfab — Generic passenger car pack (Comrade1280)' },
  { id: 'car-hatchback',   label: 'Hatchback (4.2 m)',           category: 'vehicle', uri: '/models/3d/cars/hatchback.glb',     nativeHeightM: 1.45, defaultScale: 1,    sizeMB: 0.47, license: 'CC-BY', source: 'Sketchfab — Generic passenger car pack (Comrade1280)' },
  { id: 'car-sedan',       label: 'Sedan (4.7 m)',               category: 'vehicle', uri: '/models/3d/cars/sedan.glb',         nativeHeightM: 1.45, defaultScale: 1,    sizeMB: 0.5, license: 'CC-BY', source: 'Sketchfab — Generic passenger car pack (Comrade1280)' },
  { id: 'car-wagon',       label: 'Estate car (4.75 m)',         category: 'vehicle', uri: '/models/3d/cars/wagon.glb',         nativeHeightM: 1.50, defaultScale: 1,    sizeMB: 0.47, license: 'CC-BY', source: 'Sketchfab — Generic passenger car pack (Comrade1280)' },
  { id: 'car-coupe',       label: 'Coupe (4.5 m)',               category: 'vehicle', uri: '/models/3d/cars/coupe.glb',         nativeHeightM: 1.37, defaultScale: 1,    sizeMB: 0.43, license: 'CC-BY', source: 'Sketchfab — Generic passenger car pack (Comrade1280)' },
  { id: 'car-sport',       label: 'Sports car (4.4 m)',          category: 'vehicle', uri: '/models/3d/cars/sport.glb',         nativeHeightM: 1.18, defaultScale: 1,    sizeMB: 0.45, license: 'CC-BY', source: 'Sketchfab — Generic passenger car pack (Comrade1280)' },
  { id: 'car-suv',         label: 'SUV (4.7 m)',                 category: 'vehicle', uri: '/models/3d/cars/suv.glb',           nativeHeightM: 1.77, defaultScale: 1,    sizeMB: 0.5, license: 'CC-BY', source: 'Sketchfab — Generic passenger car pack (Comrade1280)' },
  { id: 'car-offroad',     label: 'Off-roader (4.6 m)',          category: 'vehicle', uri: '/models/3d/cars/offroad.glb',       nativeHeightM: 1.83, defaultScale: 1,    sizeMB: 0.43, license: 'CC-BY', source: 'Sketchfab — Generic passenger car pack (Comrade1280)' },
  { id: 'car-minivan',     label: 'Minivan (4.9 m)',             category: 'vehicle', uri: '/models/3d/cars/minivan.glb',       nativeHeightM: 1.87, defaultScale: 1,    sizeMB: 0.47, license: 'CC-BY', source: 'Sketchfab — Generic passenger car pack (Comrade1280)' },
  { id: 'car-pickup',      label: 'Pickup truck (5.3 m)',        category: 'vehicle', uri: '/models/3d/cars/pickup.glb',        nativeHeightM: 1.74, defaultScale: 1,    sizeMB: 0.46, license: 'CC-BY', source: 'Sketchfab — Generic passenger car pack (Comrade1280)' },
  { id: 'truck-rigid',        label: 'Rigid box truck with tail lift (10.4 m)', category: 'vehicle', uri: '/models/3d/trucks/truck-rigid.glb', nativeHeightM: 4.21, defaultScale: 1,    sizeMB: 2.36, license: 'Purchased, royalty-free', source: 'BigTruck_06 (SKU0411), purchased asset; cab and cargo split, lamps, plate and CCTVplanner livery added in Blender for this project (tools/truck-models/build_bigtruck.py)' },
  { id: 'truck-semi',         label: 'Articulated lorry, DAF XF105 with a 40 ft container (15.6 m)', category: 'vehicle', uri: '/models/3d/trucks/truck-semi-v2.glb',   nativeHeightM: 3.91, defaultScale: 1,    sizeMB: 1.83, license: 'CC-BY', source: 'Sketchfab: low poly DAF XF105 by Alvin.Woodly (tractor unit); container chassis, container and livery modelled in Blender for this project (tools/truck-models)' },
];

/** ─── MACHINERY ────────────────────────────────────────────────────
 *  Emptied by the 2026-08-17 audit: `excavator-komatsu`, `crane-construction`. */
const MACHINERY: AssetModel[] = [];

/** ─── BUILDINGS ────────────────────────────────────────────────────
 *  Emptied by the 2026-08-17 audit: `garage-modern`, `city-pack`,
 *  `industrial-pack`. A site's buildings are drawn as outlines in the 2D plan
 *  and extruded in 3D, which is what every project actually does. */
const BUILDINGS: AssetModel[] = [];

/** ─── TREES ────────────────────────────────────────────────────────
 *  Emptied by the 2026-08-17 audit: `tree-detailed.glb` (HYPHEN), 4.4 MB and
 *  never placed. Trees users do place — 1 609 of them across 91 projects — are
 *  the 16 Kenney species in `treeSpecies.ts`, which resolve by constructed path
 *  and weigh 250 KB for the whole set. */
const TREES: AssetModel[] = [];

/** ─── PEOPLE ───────────────────────────────────────────────────────
 *  Emptied by the 2026-08-17 audit: `people-pack-sample`. The DORI person
 *  target still ships (`people/person-realistic.glb`) and is resolved by
 *  `PersonGeometry`, not from here. */
const PEOPLE: AssetModel[] = [];

/** ─── PROPS ────────────────────────────────────────────────────────
 *  Emptied by the 2026-08-17 audit — all ten, none ever placed: barrier-boom,
 *  gate-sliding, turnstile, lamp-post, container-simple, container-detailed,
 *  porta-potty, cash-register, store-shelves, road-section. Note the designer
 *  has first-class entities for several of these (gates, boom barriers,
 *  containers as obstacles), which is the likeliest reason nobody reached for a
 *  static prop model. */
const PROPS: AssetModel[] = [];

/** Flat list — all categories concatenated. Indexed by id. */
export const ASSET_CATALOG: AssetModel[] = [
  ...CAMERAS,
  ...VEHICLES,
  ...MACHINERY,
  ...TREES,
  ...PEOPLE,
  ...BUILDINGS,
  ...PROPS,
];

/** O(N) lookup over a handful of entries — used at render time per obstacle
 *  to resolve modelUri → metadata. If this ever grows to 1000+ entries
 *  cache it into a Map.
 *
 *  ⚠️ Returns undefined for a model that has since been deleted from the
 *  catalog. Callers already treat undefined as "render the type default", which
 *  is what keeps an old project pointing at a removed .glb from crashing the
 *  scene — verified against production: no saved project does. */
export function getAssetByUri(uri: string | undefined): AssetModel | undefined {
  if (!uri) return undefined;
  return ASSET_CATALOG.find((a) => a.uri === uri);
}

export function getAssetById(id: string): AssetModel | undefined {
  return ASSET_CATALOG.find((a) => a.id === id);
}

/** Group catalog by category — used by the browser UI for tab/section
 *  rendering. Returned object's iteration order matches the category
 *  ordering above (cameras first, props last). */
export function groupByCategory(): Record<AssetCategory, AssetModel[]> {
  const out: Record<AssetCategory, AssetModel[]> = {
    camera: [],
    vehicle: [],
    machinery: [],
    tree: [],
    person: [],
    building: [],
    prop: [],
  };
  for (const a of ASSET_CATALOG) out[a.category].push(a);
  return out;
}

/** Human-readable label for each category — used as section header in
 *  the browser UI. */
export const CATEGORY_LABEL: Record<AssetCategory, string> = {
  camera: 'Cameras & alarm',
  vehicle: 'Vehicles',
  machinery: 'Machinery',
  tree: 'Trees',
  person: 'People',
  building: 'Buildings',
  prop: 'Props',
};
